import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Backdrop,
    CircularProgress,
    Card,
    CardContent,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    Autocomplete,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Paper,
    InputAdornment,
} from "@mui/material";
import {
    Send,
    Image,
    TextFields,
    Description,
    ExpandMore,
    CloudUpload,
    Visibility,
    Title,
    Message,
    Subtitles,
} from "@mui/icons-material";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";

const SendMessage = () => {
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [fetchingTemplates, setFetchingTemplates] = useState(false);
    const [fetchingTemplateDetails, setFetchingTemplateDetails] = useState(false);
    
    // Config from backend
    const [config, setConfig] = useState(null);
    const [phoneNumberId, setPhoneNumberId] = useState("");
    
    // Templates list
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateDetails, setTemplateDetails] = useState(null);
    
    // Template message state
    const [templateMessage, setTemplateMessage] = useState({
        to: "",
        template_name: "",
        language: "en_US",
        headerParams: [], // Array of parameter values for header
        bodyParams: [], // Array of parameter values for body
        headerMediaId: "", // Media ID for header if media type
        headerMediaUrl: "", // Media URL for header if media type (fetched from media ID)
    });

    // Text message state
    const [textMessage, setTextMessage] = useState({
        to: "",
        text: "",
    });

    // Media message state
    const [mediaMessage, setMediaMessage] = useState({
        to: "",
        type: "image",
        media_id: "",
        caption: "",
    });

    // Preview state
    const [uploadingMedia, setUploadingMedia] = useState(false);
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null); // URL for previewing uploaded media

    const showNotification = useNotification();

    // Cleanup preview URL on component unmount
    useEffect(() => {
        return () => {
            if (mediaPreviewUrl) {
                URL.revokeObjectURL(mediaPreviewUrl);
            }
        };
    }, [mediaPreviewUrl]);

    // Fetch config from backend on mount
    useEffect(() => {
        fetchConfig();
        fetchTemplates();
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await axiosInstance.get(`${base_url}/whatsapp-business-config`);
            if (response.data) {
                setConfig(response.data);
                if (response.data.phone_number_id) {
                    setPhoneNumberId(response.data.phone_number_id);
                }
            }
        } catch (error) {
            console.log("No config found or error fetching config");
        }
    };

    const fetchTemplates = async () => {
        setFetchingTemplates(true);
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/templates`,
                {
                    params: {
                        save_to_db: false, // Just fetch, don't save
                    },
                }
            );

            if (response.data?.data) {
                // Filter only APPROVED templates
                const approvedTemplates = response.data.data.filter(
                    (t) => t.status === "APPROVED"
                );
                setTemplates(approvedTemplates);
            } else {
                setTemplates(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch templates",
                "error"
            );
        } finally {
            setFetchingTemplates(false);
        }
    };

    // Fetch template details when template is selected
    useEffect(() => {
        if (selectedTemplate) {
            console.log("Template selected:", selectedTemplate);
            // Try both template_id and id fields
            const templateId = selectedTemplate.template_id || selectedTemplate.id;
            console.log("Template ID to fetch:", templateId);
            if (templateId) {
                fetchTemplateDetails(templateId);
            } else {
                console.error("Template ID not found in selected template:", selectedTemplate);
                showNotification("Template ID not found. Please select a valid template.", "error");
            }
        } else {
            setTemplateDetails(null);
            // Reset parameters
            setTemplateMessage((prev) => ({
                ...prev,
                headerParams: [],
                bodyParams: [],
                headerMediaId: "",
                headerMediaUrl: "",
            }));
            // Clean up preview URL when template is deselected
            if (mediaPreviewUrl) {
                URL.revokeObjectURL(mediaPreviewUrl);
                setMediaPreviewUrl(null);
            }
            setMediaFile(null);
        }
    }, [selectedTemplate]);

    const fetchTemplateDetails = async (templateId) => {
        setFetchingTemplateDetails(true);
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/template/${templateId}/details`,
                {
                    params: {
                        fields: "id,name,language,category,status,components",
                    },
                }
            );

            setTemplateDetails(response.data);
            
            // Auto-populate language
            if (response.data.language) {
                setTemplateMessage((prev) => ({
                    ...prev,
                    language: response.data.language,
                }));
            }

            // Initialize parameter arrays based on template structure
            const components = response.data.components || [];
            const headerComponent = components.find((c) => c.type === "HEADER");
            const bodyComponent = components.find((c) => c.type === "BODY");

            let headerParamCount = 0;
            let bodyParamCount = 0;

            if (headerComponent) {
                if (headerComponent.format === "TEXT" && headerComponent.text) {
                    headerParamCount = extractParameterCount(headerComponent.text);
                } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format)) {
                    headerParamCount = 1; // Media header needs 1 media parameter
                }
            }

            if (bodyComponent && bodyComponent.text) {
                bodyParamCount = extractParameterCount(bodyComponent.text);
            }
            if (response.data.category === "AUTHENTICATION") {
                bodyParamCount = Math.max(bodyParamCount, 1);
            }

            setTemplateMessage((prev) => ({
                ...prev,
                headerParams: new Array(headerParamCount).fill(""),
                bodyParams: new Array(bodyParamCount).fill(""),
            }));
        } catch (error) {
            console.error("Error fetching template details:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch template details",
                "error"
            );
        } finally {
            setFetchingTemplateDetails(false);
        }
    };

    // Extract parameter count from text (e.g., {{1}}, {{2}})
    const extractParameterCount = (text) => {
        if (!text) return 0;
        const matches = text.match(/\{\{(\d+)\}\}/g) || [];
        const paramNumbers = matches.map((match) =>
            parseInt(match.replace(/\{\{|\}\}/g, ""))
        );
        return paramNumbers.length > 0 ? Math.max(...paramNumbers) : 0;
    };

    // Get header component from template details
    const headerComponent = useMemo(() => {
        if (!templateDetails?.components) return null;
        return templateDetails.components.find((c) => c.type === "HEADER");
    }, [templateDetails]);

    // Get body component from template details
    const bodyComponent = useMemo(() => {
        if (!templateDetails?.components) return null;
        return templateDetails.components.find((c) => c.type === "BODY");
    }, [templateDetails]);

    // Get footer component from template details
    const footerComponent = useMemo(() => {
        if (!templateDetails?.components) return null;
        return templateDetails.components.find((c) => c.type === "FOOTER");
    }, [templateDetails]);

    // Handle media upload for header - using WhatsApp API uploadMedia endpoint
    const handleMediaUpload = async () => {
        if (!mediaFile) {
            showNotification("Please select a file", "error");
            return;
        }

        // Phone Number ID is required for media upload
        const phoneNumberIdToUse = phoneNumberId.trim() || (config?.phone_number_id || "");
        if (!phoneNumberIdToUse) {
            showNotification("Please enter Phone Number ID or configure it in Settings", "error");
            return;
        }

        if (!headerComponent) {
            showNotification("No header component found in template", "error");
            return;
        }

        const mediaType = headerComponent.format.toLowerCase(); // IMAGE -> image
        setUploadingMedia(true);
        try {
            const formData = new FormData();
            formData.append("file", mediaFile);
            formData.append("type", mediaType);

            // Use WhatsApp API endpoint: POST /{phone_number_id}/media/upload
            // This returns a media ID that can be used in template messages
            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/phone-number/${phoneNumberIdToUse}/media/upload`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            // Response contains media ID
            const mediaId = response.data?.id;
            if (mediaId) {
                setTemplateMessage((prev) => ({
                    ...prev,
                    headerMediaId: mediaId, // Use media ID for sending template message
                    headerMediaUrl: "", // URL not needed for sending, only ID is required
                }));
                showNotification("Media uploaded successfully! Media ID: " + mediaId.substring(0, 20) + "...", "success");
            } else {
                showNotification("Failed to get media ID from upload response", "error");
            }
        } catch (error) {
            console.error("Error uploading media:", error);
            showNotification(
                error.response?.data?.error || error.response?.data?.message || "Failed to upload media",
                "error"
            );
        } finally {
            setUploadingMedia(false);
        }
    };

    // Build components array for API
    const buildComponents = () => {
        const components = [];

        // Header component
        if (headerComponent) {
            if (headerComponent.format === "TEXT" && headerComponent.text) {
                const headerParams = templateMessage.headerParams
                    .filter((p) => p && p.trim())
                    .map((text) => ({
                        type: "text",
                        text: text.trim(),
                    }));

                if (headerParams.length > 0) {
                    components.push({
                        type: "HEADER",
                        parameters: headerParams,
                    });
                }
            } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format)) {
                // Media header
                if (templateMessage.headerMediaId) {
                    components.push({
                        type: "HEADER",
                        parameters: [
                            {
                                type: headerComponent.format.toLowerCase(),
                                [headerComponent.format.toLowerCase()]: {
                                    id: templateMessage.headerMediaId,
                                },
                            },
                        ],
                    });
                }
            }
        }

        // Body component
        if (bodyComponent && bodyComponent.text) {
            const bodyParams = templateMessage.bodyParams
                .filter((p) => p && p.trim())
                .map((text) => ({
                    type: "text",
                    text: text.trim(),
                }));

            if (bodyParams.length > 0) {
                components.push({
                    type: "BODY",
                    parameters: bodyParams,
                });
            }
        }

        return components;
    };

    const handleSendTemplate = async () => {
        if (!templateMessage.to || !templateMessage.template_name) {
            showNotification("Please fill all required fields", "error");
            return;
        }

        // Phone Number ID can be optional if stored in config
        const phoneNumberIdToUse = phoneNumberId.trim() || (config?.phone_number_id || "");
        if (!phoneNumberIdToUse) {
            showNotification("Please enter Phone Number ID or configure it in Settings", "error");
            return;
        }

        // Validate parameters are filled
        if (headerComponent) {
            if (headerComponent.format === "TEXT") {
                const requiredParams = templateMessage.headerParams.filter((p) => p && p.trim());
                if (requiredParams.length !== templateMessage.headerParams.length) {
                    showNotification("Please fill all header parameters", "error");
                    return;
                }
            } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format)) {
                if (!templateMessage.headerMediaId) {
                    showNotification("Please upload media for header", "error");
                    return;
                }
            }
        }

        if (bodyComponent) {
            const requiredParams = templateMessage.bodyParams.filter((p) => p && p.trim());
            if (requiredParams.length !== templateMessage.bodyParams.length) {
                showNotification("Please fill all body parameters", "error");
                return;
            }
        }

        setLoading(true);
        try {
            // Build components array
            // Note: For template messages, we only need the media ID (not URL)
            // The media ID is already stored in templateMessage.headerMediaId after upload
            const components = buildComponents();

            const payload = {
                to: templateMessage.to,
                template: {
                    name: templateMessage.template_name,
                    language: {
                        code: templateMessage.language,
                    },
                    components: components,
                },
                save_to_db: true,
            };

            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/phone-number/${phoneNumberIdToUse}/send-template`,
                payload
            );

            showNotification("Template message sent successfully!", "success");
            setTemplateMessage({
                to: "",
                template_name: "",
                language: "en_US",
                headerParams: [],
                bodyParams: [],
                headerMediaId: "",
                headerMediaUrl: "",
            });
            setSelectedTemplate(null);
            setTemplateDetails(null);
            setMediaFile(null);
            // Clean up preview URL
            if (mediaPreviewUrl) {
                URL.revokeObjectURL(mediaPreviewUrl);
                setMediaPreviewUrl(null);
            }
        } catch (error) {
            console.error("Error sending template message:", error);
            
            // Parse error message
            let errorMessage = "Failed to send template message";
            if (error.response?.data) {
                const errorData = error.response.data;
                if (errorData.data?.error?.error_user_msg) {
                    errorMessage = errorData.data.error.error_user_msg;
                } else if (errorData.data?.error?.message) {
                    errorMessage = errorData.data.error.message;
                } else if (errorData.error) {
                    errorMessage = typeof errorData.error === 'string' 
                        ? errorData.error 
                        : (errorData.error.message || errorData.error.error_user_msg || errorMessage);
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                }
            }
            
            showNotification(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSendText = async () => {
        if (!textMessage.to || !textMessage.text) {
            showNotification("Please fill all required fields", "error");
            return;
        }

        // Phone Number ID can be optional if stored in config
        const phoneNumberIdToUse = phoneNumberId.trim() || (config?.phone_number_id || "");
        if (!phoneNumberIdToUse) {
            showNotification("Please enter Phone Number ID or configure it in Settings", "error");
            return;
        }

        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const payload = {
                to: textMessage.to,
                text: textMessage.text,
                save_to_db: true,
            };

            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/phone-number/${phoneNumberIdToUse}/send-text`,
                payload
            );

            showNotification("Text message sent successfully!", "success");
            setTextMessage({ to: "", text: "" });
        } catch (error) {
            console.error("Error sending text message:", error);
            showNotification(
                error.response?.data?.error || "Failed to send text message",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleSendMedia = async () => {
        if (!mediaMessage.to || !mediaMessage.media_id) {
            showNotification("Please fill all required fields", "error");
            return;
        }

        // Phone Number ID can be optional if stored in config
        const phoneNumberIdToUse = phoneNumberId.trim() || (config?.phone_number_id || "");
        if (!phoneNumberIdToUse) {
            showNotification("Please enter Phone Number ID or configure it in Settings", "error");
            return;
        }

        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const payload = {
                to: mediaMessage.to,
                type: mediaMessage.type,
                media_id: mediaMessage.media_id,
                caption: mediaMessage.caption || undefined,
                save_to_db: true,
            };

            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/phone-number/${phoneNumberIdToUse}/send-media`,
                payload
            );

            showNotification("Media message sent successfully!", "success");
            setMediaMessage({
                to: "",
                type: "image",
                media_id: "",
                caption: "",
            });
        } catch (error) {
            console.error("Error sending media message:", error);
            showNotification(
                error.response?.data?.error || "Failed to send media message",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    // Replace parameters in text with actual values
    const replaceParameters = (text, params) => {
        if (!text) return "";
        let result = text;
        params.forEach((param, index) => {
            result = result.replace(new RegExp(`\\{\\{${index + 1}\\}\\}`, "g"), param || `{{${index + 1}}}`);
        });
        return result;
    };

    return (
        <Box sx={WB_SX.page}>
            <Typography
                variant="h6"
                sx={WB_SX.pageTitle}
            >
                Send Messages
            </Typography>

            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                    flexDirection: "column",
                    gap: 2,
                })}
                open={loading || uploadingMedia}
            >
                <CircularProgress color="inherit" size={60} />
                {uploadingMedia && (
                    <>
                        <Typography variant="h6" sx={{ mt: 2 }}>
                            Uploading Media to WhatsApp...
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8, textAlign: "center", maxWidth: 400 }}>
                            Creating upload session and uploading file. This may take a few moments...
                        </Typography>
                    </>
                )}
                {loading && !uploadingMedia && (
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Sending Message...
                    </Typography>
                )}
            </Backdrop>

            <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                <CardContent>
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <TextField
                            label="Phone Number ID"
                            variant="outlined"
                            size="small"
                            value={phoneNumberId}
                            onChange={(e) => setPhoneNumberId(e.target.value)}
                            placeholder={config?.phone_number_id || "Enter Phone Number ID"}
                            sx={{ flex: 1, minWidth: "200px" }}
                            helperText={config?.phone_number_id ? "Using saved value. Enter to override." : "Enter Phone Number ID or configure in Settings"}
                            InputProps={{
                                startAdornment: config?.phone_number_id && !phoneNumberId ? (
                                    <InputAdornment position="start">
                                        <Chip label="From Config" size="small" color="primary" />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={fetchTemplates}
                            disabled={fetchingTemplates}
                            startIcon={<Description />}
                            sx={WB_SX.secondaryButton}
                        >
                            {fetchingTemplates ? "Loading..." : "Refresh Templates"}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={WB_SX.card}>
                <CardContent>
                    <Tabs
                        value={tabValue}
                        onChange={(e, newValue) => setTabValue(newValue)}
                        sx={{
                            mb: 3,
                            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
                            "& .Mui-selected": { color: "#111827" },
                            "& .MuiTabs-indicator": { backgroundColor: "#111827" },
                        }}
                    >
                        <Tab icon={<Description />} label="Template Message" />
                        <Tab icon={<TextFields />} label="Text Message" />
                        <Tab icon={<Image />} label="Media Message" />
                    </Tabs>

                    {/* Template Message Tab */}
                    {tabValue === 0 && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Recipient Phone Number"
                                    value={templateMessage.to}
                                    onChange={(e) =>
                                        setTemplateMessage({
                                            ...templateMessage,
                                            to: e.target.value,
                                        })
                                    }
                                    placeholder="e.g., 1234567890 (with country code)"
                                    required
                                    helperText="Include country code without + sign"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={templates}
                                    getOptionLabel={(option) => option.name || ""}
                                    value={selectedTemplate}
                                    onChange={(event, newValue) => {
                                        setSelectedTemplate(newValue);
                                        if (newValue) {
                                            setTemplateMessage((prev) => ({
                                                ...prev,
                                                template_name: newValue.name,
                                            }));
                                        }
                                    }}
                                    loading={fetchingTemplates}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Template"
                                            placeholder="Search and select a template"
                                            required
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {fetchingTemplates ? (
                                                            <CircularProgress color="inherit" size={20} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                                <Typography variant="body1">{option.name}</Typography>
                                                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                                                    <Chip
                                                        label={option.category}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={option.language}
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={option.status}
                                                        size="small"
                                                        color={
                                                            option.status === "APPROVED"
                                                                ? "success"
                                                                : option.status === "PENDING"
                                                                ? "warning"
                                                                : "error"
                                                        }
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Language</InputLabel>
                                    <Select
                                        value={templateMessage.language}
                                        onChange={(e) =>
                                            setTemplateMessage({
                                                ...templateMessage,
                                                language: e.target.value,
                                            })
                                        }
                                        disabled={!!selectedTemplate} // Disable if template is selected
                                    >
                                        <MenuItem value="en_US">English (US)</MenuItem>
                                        <MenuItem value="en_GB">English (UK)</MenuItem>
                                        <MenuItem value="hi">Hindi</MenuItem>
                                        <MenuItem value="es">Spanish</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Template Details and Parameters */}
                            {fetchingTemplateDetails && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="body2" sx={{ ml: 2 }}>
                                            Loading template details...
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}

                            {templateDetails && !fetchingTemplateDetails && (
                                <>
                                    {/* Header Parameters */}
                                    {headerComponent && (
                                        <Grid item xs={12}>
                                            <Accordion defaultExpanded>
                                                <AccordionSummary expandIcon={<ExpandMore />}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Title fontSize="small" />
                                                        <Typography variant="subtitle2">
                                                            Header Parameters
                                                            {headerComponent.format !== "TEXT" && (
                                                                <Chip
                                                                    label={headerComponent.format}
                                                                    size="small"
                                                                    sx={{ ml: 1 }}
                                                                />
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    {headerComponent.format === "TEXT" && headerComponent.text && (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                                                                Template: {headerComponent.text}
                                                            </Typography>
                                                            <Grid container spacing={2}>
                                                                {templateMessage.headerParams.map((param, index) => (
                                                                    <Grid item xs={12} sm={6} key={index}>
                                                                        <TextField
                                                                            fullWidth
                                                                            size="small"
                                                                            label={`Header Parameter {{${index + 1}}}`}
                                                                            value={param}
                                                                            onChange={(e) => {
                                                                                const newParams = [...templateMessage.headerParams];
                                                                                newParams[index] = e.target.value;
                                                                                setTemplateMessage((prev) => ({
                                                                                    ...prev,
                                                                                    headerParams: newParams,
                                                                                }));
                                                                            }}
                                                                            placeholder={`Enter value for {{${index + 1}}}`}
                                                                            required
                                                                        />
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </Box>
                                                    )}
                                                    {["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format) && (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                                                                Upload {headerComponent.format.toLowerCase()} for header
                                                            </Typography>
                                                            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                                                <input
                                                                    accept={
                                                                        headerComponent.format === "IMAGE"
                                                                            ? "image/*"
                                                                            : headerComponent.format === "VIDEO"
                                                                            ? "video/*"
                                                                            : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                                                    }
                                                                    style={{ display: "none" }}
                                                                    id="header-media-upload"
                                                                    type="file"
                                                                    onChange={(e) => {
                                                                        const file = e.target.files[0];
                                                                        if (file) {
                                                                            setMediaFile(file);
                                                                            // Create preview URL from file
                                                                            const previewUrl = URL.createObjectURL(file);
                                                                            setMediaPreviewUrl(previewUrl);
                                                                        }
                                                                    }}
                                                                />
                                                                <label htmlFor="header-media-upload">
                                                                    <Button
                                                                        variant="outlined"
                                                                        component="span"
                                                                        size="small"
                                                                        startIcon={<CloudUpload />}
                                                                        disabled={uploadingMedia}
                                                                    >
                                                                        {mediaFile ? mediaFile.name : "Select File"}
                                                                    </Button>
                                                                </label>
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    onClick={handleMediaUpload}
                                                                    disabled={!mediaFile || uploadingMedia}
                                                                    startIcon={uploadingMedia ? <CircularProgress size={16} /> : <CloudUpload />}
                                                                >
                                                                    {uploadingMedia ? "Uploading..." : "Upload"}
                                                                </Button>
                                                                {templateMessage.headerMediaId && (
                                                                    <Chip
                                                                        label={`Media ID: ${templateMessage.headerMediaId}`}
                                                                        size="small"
                                                                        color="success"
                                                                        onDelete={() => {
                                                                            setTemplateMessage((prev) => ({
                                                                                ...prev,
                                                                                headerMediaId: "",
                                                                                headerMediaUrl: "",
                                                                            }));
                                                                            setMediaFile(null);
                                                                            // Clean up preview URL
                                                                            if (mediaPreviewUrl) {
                                                                                URL.revokeObjectURL(mediaPreviewUrl);
                                                                                setMediaPreviewUrl(null);
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        </Grid>
                                    )}

                                    {/* Body Parameters */}
                                    {((bodyComponent && bodyComponent.text) || templateDetails?.category === "AUTHENTICATION") && (
                                        <Grid item xs={12}>
                                            <Accordion defaultExpanded>
                                                <AccordionSummary expandIcon={<ExpandMore />}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Message fontSize="small" />
                                                        <Typography variant="subtitle2">
                                                            Body Parameters
                                                        </Typography>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                                                        {templateDetails?.category === "AUTHENTICATION"
                                                            ? "Enter the OTP. It is sent in BODY; the OTP button is added automatically."
                                                            : `Template: ${bodyComponent.text}`}
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        {templateMessage.bodyParams.map((param, index) => (
                                                            <Grid item xs={12} sm={6} key={index}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    label={
                                                                        templateDetails?.category === "AUTHENTICATION"
                                                                            ? "OTP / verification code"
                                                                            : `Body Parameter {{${index + 1}}}`
                                                                    }
                                                                    value={param}
                                                                    onChange={(e) => {
                                                                        const newParams = [...templateMessage.bodyParams];
                                                                        newParams[index] = e.target.value;
                                                                        setTemplateMessage((prev) => ({
                                                                            ...prev,
                                                                            bodyParams: newParams,
                                                                        }));
                                                                    }}
                                                                    placeholder={
                                                                        templateDetails?.category === "AUTHENTICATION"
                                                                            ? "e.g. 123456"
                                                                            : `Enter value for {{${index + 1}}}`
                                                                    }
                                                                    required
                                                                    inputProps={
                                                                        templateDetails?.category === "AUTHENTICATION"
                                                                            ? { maxLength: 15 }
                                                                            : undefined
                                                                    }
                                                                />
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </AccordionDetails>
                                            </Accordion>
                                        </Grid>
                                    )}

                                    {/* WhatsApp Preview Section */}
                                    <Grid item xs={12}>
                                        <Accordion defaultExpanded>
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Visibility fontSize="small" />
                                                    <Typography variant="subtitle2">WhatsApp Message Preview</Typography>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Box
                                                    sx={{
                                                        backgroundColor: "#e5ddd5",
                                                        minHeight: "400px",
                                                        borderRadius: 2,
                                                        p: 2,
                                                        backgroundImage: 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAAdgAAAHYBTnsmCAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3Njape.org5vuPBoAAAAnSURBVHic7cEBDQAAAMIg+6c2BL4mAAAAAAAAAAAAAAAAAAD4B7a4AAFj8b8zAAAAAElFTkSuQmCC")',
                                                        display: "flex",
                                                        flexDirection: "column",
                                                    }}
                                                >
                                                    {/* WhatsApp Header */}
                                                    <Box
                                                        sx={{
                                                            backgroundColor: "#075e54",
                                                            color: "white",
                                                            p: 2,
                                                            borderRadius: "10px 10px 0 0",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 2,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                backgroundColor: "#25d366",
                                                                borderRadius: "50%",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            WA
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="subtitle1" fontWeight="bold">
                                                                Business Account
                                                            </Typography>
                                                            <Typography variant="caption">WhatsApp Business</Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Chat Area */}
                                                    <Box sx={{ flex: 1, p: 2, overflow: "auto" }}>
                                                        {/* Template Message - Received (Left side, white background) */}
                                                        <Paper
                                                            sx={{
                                                                maxWidth: "70%",
                                                                mr: "auto",
                                                                mb: 2,
                                                                backgroundColor: "#ffffff",
                                                                border: "1px solid #e5e5e5",
                                                                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                                            }}
                                                        >
                                                            <Box sx={{ p: 1.5 }}>
                                                                {/* Header Preview */}
                                                                {headerComponent && (
                                                                    <>
                                                                        {headerComponent.format === "TEXT" && headerComponent.text && (
                                                                            <Typography
                                                                                variant="subtitle2"
                                                                                fontWeight="bold"
                                                                                sx={{ mb: 1, color: "#075e54" }}
                                                                            >
                                                                                {replaceParameters(
                                                                                    headerComponent.text,
                                                                                    templateMessage.headerParams
                                                                                )}
                                                                            </Typography>
                                                                        )}
                                                                        {["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format) && (
                                                                            <>
                                                                                {mediaPreviewUrl && headerComponent.format === "IMAGE" && (
                                                                                    <Box
                                                                                        sx={{
                                                                                            mb: 1,
                                                                                            borderRadius: 1,
                                                                                            overflow: "hidden",
                                                                                            maxWidth: "100%",
                                                                                        }}
                                                                                    >
                                                                                        <Box
                                                                                            component="img"
                                                                                            src={mediaPreviewUrl}
                                                                                            alt="Header preview"
                                                                                            sx={{
                                                                                                width: "100%",
                                                                                                maxHeight: "200px",
                                                                                                objectFit: "cover",
                                                                                                display: "block",
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                )}
                                                                                {mediaPreviewUrl && headerComponent.format === "VIDEO" && (
                                                                                    <Box
                                                                                        sx={{
                                                                                            mb: 1,
                                                                                            borderRadius: 1,
                                                                                            overflow: "hidden",
                                                                                            maxWidth: "100%",
                                                                                        }}
                                                                                    >
                                                                                        <Box
                                                                                            component="video"
                                                                                            src={mediaPreviewUrl}
                                                                                            controls
                                                                                            sx={{
                                                                                                width: "100%",
                                                                                                maxHeight: "200px",
                                                                                                display: "block",
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                )}
                                                                                {mediaPreviewUrl && headerComponent.format === "DOCUMENT" && (
                                                                                    <Box
                                                                                        sx={{
                                                                                            mb: 1,
                                                                                            p: 1.5,
                                                                                            backgroundColor: "#f5f5f5",
                                                                                            borderRadius: 1,
                                                                                            display: "flex",
                                                                                            alignItems: "center",
                                                                                            gap: 1,
                                                                                        }}
                                                                                    >
                                                                                        <Description fontSize="small" color="primary" />
                                                                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                                                                            {mediaFile?.name || "Document uploaded"}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                )}
                                                                                {!mediaPreviewUrl && (
                                                                                    <Box
                                                                                        sx={{
                                                                                            mb: 1,
                                                                                            p: 1.5,
                                                                                            backgroundColor: "#f5f5f5",
                                                                                            borderRadius: 1,
                                                                                            display: "flex",
                                                                                            alignItems: "center",
                                                                                            gap: 1,
                                                                                        }}
                                                                                    >
                                                                                        {headerComponent.format === "IMAGE" && <Image fontSize="small" color="primary" />}
                                                                                        {headerComponent.format === "VIDEO" && <Description fontSize="small" color="primary" />}
                                                                                        {headerComponent.format === "DOCUMENT" && <Description fontSize="small" color="primary" />}
                                                                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                                                                            {templateMessage.headerMediaId
                                                                                                ? `${headerComponent.format} uploaded`
                                                                                                : `${headerComponent.format} not uploaded`}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </>
                                                                )}

                                                                {/* Body Preview */}
                                                                {bodyComponent && bodyComponent.text && (
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            lineHeight: 1.4,
                                                                            whiteSpace: "pre-wrap",
                                                                        }}
                                                                    >
                                                                        {replaceParameters(
                                                                            bodyComponent.text,
                                                                            templateMessage.bodyParams
                                                                        )}
                                                                    </Typography>
                                                                )}

                                                                {/* Footer Preview */}
                                                                {footerComponent && footerComponent.text && (
                                                                    <>
                                                                        <Divider sx={{ my: 1 }} />
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {footerComponent.text}
                                                                        </Typography>
                                                                    </>
                                                                )}

                                                                <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        {new Date().toLocaleTimeString([], {
                                                                            hour: "2-digit",
                                                                            minute: "2-digit",
                                                                        })}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Paper>

                                                        {/* Info Message */}
                                                        <Paper
                                                            sx={{
                                                                maxWidth: "80%",
                                                                ml: "auto",
                                                                backgroundColor: "#dcf8c6",
                                                                border: "1px solid #e5e5e5",
                                                            }}
                                                        >
                                                            <Box sx={{ p: 1.5 }}>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    This is a preview of how your template message will look.
                                                                </Typography>
                                                            </Box>
                                                        </Paper>
                                                    </Box>

                                                    {/* WhatsApp Footer */}
                                                    <Box
                                                        sx={{
                                                            backgroundColor: "#f0f0f0",
                                                            p: 1,
                                                            borderRadius: "0 0 10px 10px",
                                                            textAlign: "center",
                                                        }}
                                                    >
                                                        <Typography variant="caption" color="textSecondary">
                                                            Template message • No replies
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    onClick={handleSendTemplate}
                                    startIcon={<Send />}
                                    disabled={!selectedTemplate || !templateMessage.to || fetchingTemplateDetails}
                                    sx={WB_SX.primaryButton}
                                >
                                    Send Template Message
                                </Button>
                            </Grid>
                        </Grid>
                    )}

                    {/* Text Message Tab */}
                    {tabValue === 1 && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Recipient Phone Number"
                                    value={textMessage.to}
                                    onChange={(e) =>
                                        setTextMessage({ ...textMessage, to: e.target.value })
                                    }
                                    placeholder="e.g., 1234567890"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Message Text"
                                    value={textMessage.text}
                                    onChange={(e) =>
                                        setTextMessage({ ...textMessage, text: e.target.value })
                                    }
                                    multiline
                                    rows={4}
                                    placeholder="Enter your message"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    onClick={handleSendText}
                                    startIcon={<Send />}
                                    sx={WB_SX.primaryButton}
                                >
                                    Send Text Message
                                </Button>
                            </Grid>
                        </Grid>
                    )}

                    {/* Media Message Tab */}
                    {tabValue === 2 && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Recipient Phone Number"
                                    value={mediaMessage.to}
                                    onChange={(e) =>
                                        setMediaMessage({ ...mediaMessage, to: e.target.value })
                                    }
                                    placeholder="e.g., 1234567890"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Media Type</InputLabel>
                                    <Select
                                        value={mediaMessage.type}
                                        onChange={(e) =>
                                            setMediaMessage({
                                                ...mediaMessage,
                                                type: e.target.value,
                                            })
                                        }
                                    >
                                        <MenuItem value="image">Image</MenuItem>
                                        <MenuItem value="video">Video</MenuItem>
                                        <MenuItem value="document">Document</MenuItem>
                                        <MenuItem value="audio">Audio</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Media ID"
                                    value={mediaMessage.media_id}
                                    onChange={(e) =>
                                        setMediaMessage({
                                            ...mediaMessage,
                                            media_id: e.target.value,
                                        })
                                    }
                                    placeholder="Enter media ID (upload media first)"
                                    required
                                    helperText="Upload media using Media Management to get Media ID"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Caption (Optional)"
                                    value={mediaMessage.caption}
                                    onChange={(e) =>
                                        setMediaMessage({
                                            ...mediaMessage,
                                            caption: e.target.value,
                                        })
                                    }
                                    multiline
                                    rows={2}
                                    placeholder="Enter caption"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    onClick={handleSendMedia}
                                    startIcon={<Send />}
                                    sx={WB_SX.primaryButton}
                                >
                                    Send Media Message
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default SendMessage;

